import { Fragment, useEffect, useMemo, useState, useRef } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import { formatDate } from "src/Common/common";
import { vocabularize } from "src/Common/vocab";
import Loader from "src/Components/Common/Loader";
import DateRangeSelector from "src/Pages/Setting/Report/components/DateRangeSelector";
import ReportFiltersSection, { EMPTY_REPORT_FILTERS } from "src/Pages/Setting/Report/components/ReportFiltersSection";

import BranchExclusionEditor from "./BranchExclusionEditor";
import OtRuleDrawer from "./OtRuleDrawer";
import LunchRulesDrawer from "./LunchRulesDrawer";
import HoursSystemDrawer from "./HoursSystemDrawer";
import HoursHistoryDrawer from "./HoursHistoryDrawer";
import TechnicianRow from "./TechnicianRow";
import BranchService from "src/Services/API/branch.service";
import ReportService from "src/Services/API/report.service";
import { handleError } from "src/Common/function";
import UserService from "src/Services/API/user.service";
import {
  signoffWeeksFor,
  onCallDaysInRange,
  WEEK_CYCLE_BOTH,
  WEEK_CYCLE_MON_SUN,
  WEEK_CYCLE_OPTIONS,
  WEEK_CYCLE_SUN_SAT,
  cyclesIn,
  isBothCycles,
  isCrossover,
  splitByBranchCycle,
  applyDerivedOvertime,
  calendarQuarter,
  formatHhMm,
  groupByTechnician,
  lastCycleWeek,
  lastMonth,
  lastYear,
  thisCycleWeek,
  thisMonth,
  thisYear,
  totalsForTechnicians,
} from "./hours";
import useHoursWorked from "./useHoursWorked";
import { openPrintTimecards } from "./printTimecards";

// Time Management runs on pay weeks, not rolling windows. "Past 7 Days" cannot
// express one: a pay week starts on a fixed weekday, and which weekday depends
// on the branch's cycle.
const TIME_PRESETS = [
  // First, not last: payroll pulls exact pay periods far more often than any
  // of the presets, and a list that put it at the bottom was a scroll every
  // time.
  { key: "custom", label: "Custom Range", days: null },
  { key: "thisWeek", label: "This Week" },
  { key: "lastWeek", label: "Last Week" },
  { key: "thisMonth", label: "This Month" },
  { key: "lastMonth", label: "Last Month" },
  { key: "q1", label: "Q1" },
  { key: "q2", label: "Q2" },
  { key: "q3", label: "Q3" },
  { key: "q4", label: "Q4" },
  { key: "thisYear", label: "This Year" },
  { key: "lastYear", label: "Last Year" },
];

// The explicit [start, end] every preset resolves to; null only for Custom
// Range, where the user supplies the dates.
//
// Weeks come from the selected cycle; everything else is a calendar period.
// There are no rolling windows left, which is the point: "the last 90 days" and
// "Q3" answer different questions and reading one as the other is a payroll
// error. Sending explicit dates also keeps the range off the server's "today",
// which falls back to EST when no single branch is named.
const presetRange = (dateRange, weekCycle) => {
  switch (dateRange) {
    case "thisWeek":
      return thisCycleWeek(weekCycle);
    case "lastWeek":
      return lastCycleWeek(weekCycle);
    case "thisMonth":
      return thisMonth();
    case "lastMonth":
      return lastMonth();
    case "q1":
      return calendarQuarter(1);
    case "q2":
      return calendarQuarter(2);
    case "q3":
      return calendarQuarter(3);
    case "q4":
      return calendarQuarter(4);
    case "thisYear":
      return thisYear();
    case "lastYear":
      return lastYear();
    default:
      return null;
  }
};

// Resolve the date-range UI down to the flags the report service expects.
// "All" is deliberately not offered: an unbounded range would page forever
// against a 10-row preview endpoint.
const resolveDateFlags = (dateRange, customStart, customEnd, weekCycle) => {
  const range = presetRange(dateRange, weekCycle);
  const [startDate, endDate] = range || [customStart, customEnd];
  // Every range is sent explicitly. The rolling-window flags would ignore the
  // week cycle, and a calendar period cannot be expressed as a day count.
  return { past7Days: false, past30Days: false, customDays: true, startDate, endDate };
};

// The sortable numeric columns, and what each one reads off a technician.
const SORT_COLUMNS = [
  { key: "entries", label: "Entries", value: (tech) => tech.rows.length, align: "center" },
  { key: "rt", label: "RT", width: 124, value: (tech) => tech.regularMinutes, align: "center" },
  { key: "ot", label: "OT", width: 124, value: (tech) => tech.overMinutes, align: "center" },
  { key: "dt", label: "DT", width: 124, value: (tech) => tech.doubleMinutes ?? 0, align: "center" },
  { key: "tt", label: "TT", width: 124, value: (tech) => tech.travelMinutes, align: "center" },
  // Days, not hours: on call is a note on the day and is never in the total.
  { key: "onCall", label: "On call (days)", value: (tech) => tech.onCallDays ?? 0, align: "center" },
  { key: "total", label: "Total", value: (tech) => tech.totalMinutes, align: "center" },
];

// Descending first: on a payroll screen the question is almost always "who has
// the most", not "who has the least".
const nextDirection = (current) => (current === "desc" ? "asc" : current === "asc" ? null : "desc");

const SortableHeader = ({ column, sort, onSort }) => {
  const active = sort?.key === column.key;
  const direction = active ? sort.direction : null;
  return (
    <th
      // The four figure columns share one width, so the gaps between RT, OT,
      // DT and TT are equal instead of following each column's widest span.
      style={column.width ? { width: column.width, minWidth: column.width } : undefined}
      aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}
      className={`px-6 py-4 text-[12px] leading-4 font-InterBold uppercase tracking-[0.6px] ${
        column.align === "center" ? "text-center" : column.align === "right" ? "text-right" : "text-left"
      } ${active ? "text-[#0058E5]" : "text-[#1F2A44]"}`}
    >
      <button
        type="button"
        onClick={() => onSort(column.key)}
        title={
          direction === "desc"
            ? "Sorted high to low — click for low to high"
            : direction === "asc"
            ? "Sorted low to high — click to clear"
            : `Sort by ${column.label}`
        }
        className={`inline-flex items-center gap-1 uppercase tracking-[0.6px] hover:text-[#0058E5] ${
          column.align === "right" ? "flex-row-reverse" : ""
        }`}
      >
        {column.label}
        {/* Both chevrons always render, the active one highlighted, so the
            header does not change width when a column is sorted. */}
        <span className="flex flex-col leading-none" aria-hidden="true">
          <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
            <path
              d="M1 4L4 1l3 3"
              stroke={direction === "asc" ? "#0058E5" : "#CBD5E1"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <svg width="8" height="5" viewBox="0 0 8 5" fill="none" className="mt-[2px]">
            <path
              d="M1 1l3 3 3-3"
              stroke={direction === "desc" ? "#0058E5" : "#CBD5E1"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
    </th>
  );
};

const SummaryTile = ({ label, sublabel, value, muted }) => (
  <div className="flex-1 min-w-[130px] rounded-xl border border-[#CBD5E1] bg-white px-4 py-3 shadow-[0_1px_3px_rgba(16,24,40,0.08)]">
    <div className={`text-[22px] leading-7 font-InterBold ${muted ? "text-[#64748B]" : "text-[#1F2A44]"}`}>{value}</div>
    <div className="mt-1 text-[12px] leading-4 font-InterMedium text-[#64748B] uppercase tracking-[0.6px]">{label}</div>
    {sublabel ? <div className="mt-0.5 text-[12px] leading-4 font-InterRegular text-[#94A3B8]">{sublabel}</div> : null}
  </div>
);

/**
 * Time Management — a read-only payroll view of hours worked.
 *
 * Sources rows from the existing hours_worked_report_preview endpoint (one row
 * per technician x work order x day) and groups them per technician for a pay
 * period. Read-only by design: the punch model, derived overtime and timecard
 * approval described in backend-requirements/time-management.md do not exist
 * yet, so nothing here can be edited and OT is shown exactly as it was typed
 * into the work order timesheet.
 */
const TimeManagement = () => {
  const [filters, setFilters] = useState(EMPTY_REPORT_FILTERS);
  // Which week cycle the pay week is anchored to. Branches differ, so "this
  // week" is not one thing — a Sunday belongs to the new week under Sun-Sat and
  // to the outgoing one under Mon-Sun.
  const [weekCycle, setWeekCycle] = useState(WEEK_CYCLE_SUN_SAT);
  const [branches, setBranches] = useState([]);
  // Branches this user always leaves out, saved against their account.
  const [excludedBranchIds, setExcludedBranchIds] = useState([]);
  const [savingExclusions, setSavingExclusions] = useState(false);
  const [dateRange, setDateRange] = useState("thisWeek");
  // The technician whose hours history is open in the right-hand drawer.
  const [historyFor, setHistoryFor] = useState(null);
  // Every loaded technician's history, fetched once when the hours arrive and
  // keyed by user id, so opening a drawer is a lookup rather than a request.
  // The drawer still refreshes its own technician quietly in the background.
  const [historyByUser, setHistoryByUser] = useState({});
  // Bumped to refetch the history without reloading hours — after the policy
  // drawer closes, since a policy change writes a ledger line the drawer
  // would otherwise not show until the next Load Hours.
  const [historyRefresh, setHistoryRefresh] = useState(0);
  // On-call spans per technician for the loaded range. Beside the rows, not
  // in them: on call counts toward nothing and must never be summed with hours.
  const [onCallByUser, setOnCallByUser] = useState({});
  // Each loaded technician's weekly sign-off record: their cycle, which week
  // is last week on their clock, and the weeks they have signed. Bumping the
  // counter refetches after the office acts on one.
  const [signoffByUser, setSignoffByUser] = useState({});
  const [signoffRefresh, setSignoffRefresh] = useState(0);

  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [otRulesOpen, setOtRulesOpen] = useState(false);
  const [lunchRulesOpen, setLunchRulesOpen] = useState(false);
  const [hoursSystemOpen, setHoursSystemOpen] = useState(false);
  // Technician-weeks payroll has decided to measure as two. Seeded from the
  // server on every load and written back on every click, so the decision
  // outlives the page — it changes what someone is paid, and the next person to
  // open the screen has to see the same figures.
  const [splitTechnicians, setSplitTechnicians] = useState(() => new Set());
  const [savingSplit, setSavingSplit] = useState(false);

  // null = the default order, which is technicians by name.
  const [sort, setSort] = useState(null);

  // The args the hook is currently loading. Held in state rather than derived
  // from the filters so that editing a filter does not immediately fire a
  // multi-request walk — the user opts in with Load Hours.
  const [args, setArgs] = useState(null);
  // The range the rows on screen actually came from, captured at load rather
  // than read off the date controls. Someone can change the preset without
  // pressing Load Hours, and a printed timecard headed with a range its figures
  // do not cover is worse than one with no range on it at all.
  const [loadedRange, setLoadedRange] = useState(null);

  const { rows, loading, refreshing, refresh, recordsFound, capped, maxRows, totalsComplete, splitWeeks, otSummary } =
    useHoursWorked(args);

  // After a save from a row: fetch the range again in place. The rows,
  // totals, tiles and history all update, but nothing collapses or empties
  // and the sort and scroll stay where they were.
  const refreshHours = () => refresh();

  // Say when the refreshed figures have landed, since the rows change under
  // a dimmed table rather than after a blank one.
  const wasRefreshing = useRef(false);
  useEffect(() => {
    if (wasRefreshing.current && !refreshing) toast("Hours updated");
    wasRefreshing.current = refreshing;
  }, [refreshing]);

  // Seeded from the server on every load. Declared after the hook that supplies
  // it — the same use-before-declaration rule the ReportFiltersSection memo had
  // to learn, and cheaper to follow than to diagnose.
  useEffect(() => {
    setSplitTechnicians(new Set(splitWeeks.map((week) => `${week.technicianId}|${week.weekStart}`)));
  }, [splitWeeks]);

  // Branches are needed here (not just inside the filter section) to work out
  // which ones run the selected cycle.
  useEffect(() => {
    BranchService.getAll()
      .then((response) => setBranches(Array.isArray(response.data) ? response.data : []))
      .catch((error) => handleError(error));

    UserService.getTimeManagementPreferences()
      .then((response) => {
        const ids = response?.data?.excludedBranchIds;
        setExcludedBranchIds(Array.isArray(ids) ? ids : []);
      })
      .catch((error) => handleError(error));
  }, []);

  const saveExclusions = (next) => {
    const previous = excludedBranchIds;
    setExcludedBranchIds(next);
    setSavingExclusions(true);
    UserService.setTimeManagementPreferences(next)
      .catch((error) => {
        setExcludedBranchIds(previous);
        handleError(error);
      })
      .finally(() => setSavingExclusions(false));
  };

  // Branches on the selected cycle. A branch with no timecardCycle set counts
  // as Sunday-Saturday, matching the backend's fallback — otherwise it would
  // vanish from every selection and its hours would be unreachable.
  // Branches the pickers offer, and the set sent when nothing is chosen.
  //
  // This used to be narrowed to the selected cycle, which is what made a
  // crossover invisible: the other cycle's branches were never in the response,
  // so nothing could report that a technician had worked them. The query now
  // asks for every branch the user can see and the *display* narrows instead,
  // which costs one modest increase in rows and makes the detection exact
  // rather than approximate.
  const allowedBranchIds = useMemo(() => {
    const excluded = new Set(excludedBranchIds);
    return branches.filter((branch) => !excluded.has(branch.id)).map((branch) => branch.id);
  }, [branches, excludedBranchIds]);

  // Only the pickers are narrowed by cycle, so a person choosing a branch is
  // still offered the ones whose week they are looking at.
  const cycleBranchIds = useMemo(() => {
    const cycles = new Set(cyclesIn(weekCycle));
    const excluded = new Set(excludedBranchIds);
    return branches
      .filter((branch) => cycles.has(branch.timecardCycle === 1 ? 1 : 0))
      .filter((branch) => !excluded.has(branch.id))
      .map((branch) => branch.id);
  }, [branches, weekCycle, excludedBranchIds]);

  // Drop selections that belong to the other cycle when the cycle changes,
  // rather than leaving an invisible filter applied to hidden branches.
  useEffect(() => {
    if (!branches.length) return;
    const allowed = new Set(cycleBranchIds);
    setFilters((previous) => {
      const branchIds = previous.branchIds.filter((id) => allowed.has(id));
      if (branchIds.length === previous.branchIds.length) return previous;
      return { ...previous, branchIds };
    });
  }, [cycleBranchIds, branches.length]);

  // Totals are taken from the technician entries rather than the raw rows, so
  // that whatever the derivation decided is what gets summed. Totalling the
  // rows separately would let the tiles disagree with the table.
  const technicians = useMemo(
    () => applyDerivedOvertime(groupByTechnician(rows), otSummary),
    [rows, otSummary]
  );

  // Below the `technicians` memo on purpose: the dependency array is read
  // during render, and a const is in its temporal dead zone until its line
  // runs — placing this above it crashed the page on first render.
  useEffect(() => {
    const ids = [...new Set(technicians.map((tech) => tech.technicianId).filter((id) => id != null))];
    if (!ids.length || !loadedRange?.[0] || !loadedRange?.[1]) {
      setHistoryByUser({});
      setOnCallByUser({});
      setSignoffByUser({});
      return undefined;
    }
    let cancelled = false;
    const toDay = (value) => moment(value).format("YYYY-MM-DD");
    ReportService.getOnCallFor(ids, toDay(loadedRange[0]), toDay(loadedRange[1]))
      .then((response) => {
        if (cancelled) return;
        const byUser = {};
        for (const span of Array.isArray(response.data) ? response.data : []) {
          (byUser[span.userId] ||= []).push(span);
        }
        for (const id of ids) byUser[id] ||= [];
        setOnCallByUser(byUser);
      })
      .catch(() => {});
    ReportService.getTimecardSubmissionsFor(ids, toDay(loadedRange[0]), toDay(loadedRange[1]))
      .then((response) => {
        if (cancelled) return;
        const byUser = {};
        for (const user of response.data?.users || []) {
          byUser[user.userId] = { ...user, submissions: [] };
        }
        for (const submission of response.data?.submissions || []) {
          byUser[submission.userId]?.submissions.push(submission);
        }
        setSignoffByUser(byUser);
      })
      // Without it the chips are simply absent, as before the feature.
      .catch(() => {});
    ReportService.getHoursEventsFor(ids, toDay(loadedRange[0]), toDay(loadedRange[1]))
      .then((response) => {
        if (cancelled) return;
        const byUser = {};
        for (const event of Array.isArray(response.data) ? response.data : []) {
          (byUser[event.subjectUserId] ||= []).push(event);
        }
        // Technicians with no events get an empty list, so the drawer knows
        // "nothing recorded" from "not prefetched yet".
        for (const id of ids) byUser[id] ||= [];
        setHistoryByUser(byUser);
      })
      // A failed prefetch just means the drawer fetches on open, as before.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [technicians, loadedRange, historyRefresh, signoffRefresh]);
  // A technician belongs to one cycle — their own branch's — and all of their
  // hours are measured on it, whichever branch the work order was for. So the
  // cycle selector picks *people*, not work orders. Filtering by the branch of
  // each row instead would split a crossover technician's week in two and show
  // each half against a threshold it never met.
  // Rows are grouped by the cycle of the branch each work order belongs to, so
  // a technician who worked both appears once under each. The repetition is
  // deliberate — it is a louder signal of a crossover than a badge on one row.
  //
  // Only the display splits. Overtime is still derived per technician across
  // all of their hours: bucketing the computation this way is what loses
  // overtime when neither bucket reaches 40 on its own.
  const visibleTechnicians = useMemo(() => {
    const cycles = new Set(cyclesIn(weekCycle));
    return splitByBranchCycle(technicians, splitTechnicians)
      .filter((slice) => cycles.has(slice.cycle))
      // The count of on-call days in the loaded range rides on each row so
      // the column can show it and the sort can order by it. A person shown
      // on both cycles carries the same count on both rows.
      .map((slice) => ({
        ...slice,
        onCallDays: onCallDaysInRange(onCallByUser[slice.technicianId], loadedRange).length,
      }));
  }, [technicians, weekCycle, splitTechnicians, onCallByUser, loadedRange]);

  // Finished cycle weeks in the range, per person, and how many are signed.
  // Only weeks that have ended count: the current week cannot be signed yet.
  const signoffTotals = useMemo(() => {
    const seen = new Set();
    let weeks = 0;
    let submitted = 0;
    let requests = 0;
    for (const slice of visibleTechnicians) {
      if (slice.technicianId == null || seen.has(slice.technicianId)) continue;
      seen.add(slice.technicianId);
      for (const week of signoffWeeksFor(signoffByUser[slice.technicianId], loadedRange)) {
        // Rows from outside coverage are records, not expectations, so the
        // tile does not count them. Nor the week in progress until it is
        // signed: nothing is owed on it yet.
        if (!week.covered) continue;
        if (week.current && week.status === "OPEN") continue;
        weeks += 1;
        if (week.status === "SUBMITTED" || week.status === "REOPEN_REQUESTED") submitted += 1;
        if (week.status === "REOPEN_REQUESTED") requests += 1;
      }
    }
    return { weeks, submitted, requests };
  }, [visibleTechnicians, signoffByUser, loadedRange]);

  // Once per person, however many rows they occupy.
  const onCallDayTotal = useMemo(() => {
    const byPerson = new Map();
    for (const slice of visibleTechnicians) {
      if (slice.technicianId != null) byPerson.set(slice.technicianId, slice.onCallDays ?? 0);
    }
    return [...byPerson.values()].reduce((sum, days) => sum + days, 0);
  }, [visibleTechnicians]);

  const totals = useMemo(() => totalsForTechnicians(visibleTechnicians), [visibleTechnicians]);

  // People, not rows: someone appearing under both cycles is one technician.
  const visiblePeople = useMemo(
    () => new Set(visibleTechnicians.map((slice) => slice.technician)).size,
    [visibleTechnicians],
  );

  // Technicians whose hours span both calendars. Detected across everyone
  // loaded, not just those on screen, so a single-cycle view can still say that
  // someone it is not showing has hours on the cycle it is showing.
  const crossovers = useMemo(() => technicians.filter(isCrossover), [technicians]);
  const visibleRowCount = useMemo(
    () => visibleTechnicians.reduce((sum, tech) => sum + tech.rows.length, 0),
    [visibleTechnicians],
  );
  const visibleCrossovers = useMemo(() => visibleTechnicians.filter(isCrossover), [visibleTechnicians]);

  const toggleSort = (key) => {
    setSort((previous) => {
      const direction = nextDirection(previous?.key === key ? previous.direction : null);
      return direction ? { key, direction } : null;
    });
  };

  const sortedTechnicians = useMemo(() => {
    if (!sort) return visibleTechnicians;
    const column = SORT_COLUMNS.find((item) => item.key === sort.key);
    if (!column) return visibleTechnicians;
    const factor = sort.direction === "asc" ? 1 : -1;
    // Copied before sorting: technicians is memoised from the rows, and sorting
    // it in place would mutate that cached value.
    return [...visibleTechnicians].sort((a, b) => {
      const diff = (column.value(a) - column.value(b)) * factor;
      // Ties fall back to name so equal rows keep a stable, predictable order
      // rather than shuffling between renders.
      return diff !== 0 ? diff : a.technician.localeCompare(b.technician) || a.cycle - b.cycle;
    });
  }, [visibleTechnicians, sort]);

  // One section per cycle in view, in cycle order. A single selected cycle
  // still produces one section, which renders without a header — the grouping
  // only earns its space when there is something to tell apart.
  const cycleSections = useMemo(
    () =>
      cyclesIn(weekCycle)
        .map((cycle) => {
          const members = sortedTechnicians.filter((slice) => slice.cycle === cycle);
          return {
            cycle,
            label: cycle === WEEK_CYCLE_MON_SUN ? "Mon \u2013 Sun" : "Sun \u2013 Sat",
            technicians: members,
            totals: totalsForTechnicians(members),
          };
        })
        .filter((section) => section.technicians.length > 0),
    [sortedTechnicians, weekCycle],
  );

  // Weekly-40 technicians whose range covers only part of a cycle week: the
  // threshold was measured against an incomplete week, so the number is
  // provisional and says so.

  // Technicians whose totals include days taken as entered rather than derived.
  const preservedCount = useMemo(
    () => visibleTechnicians.filter((tech) => tech.preservedDays > 0).length,
    [visibleTechnicians]
  );

  const loadHours = () => {
    const flags = resolveDateFlags(dateRange, customStart, customEnd, weekCycle);
    if (flags.customDays && (!flags.startDate || !flags.endDate)) {
      toast("Please choose a start and end date");
      return;
    }
    setExpanded(new Set());
    setSort(null);
    setLoadedRange([flags.startDate, flags.endDate]);
    setArgs({
      past7Days: flags.past7Days,
      past30Days: flags.past30Days,
      customDays: flags.customDays,
      customStartDate: formatDate(flags.startDate, "MM/DD/YYYY"),
      customEndDate: formatDate(flags.endDate, "MM/DD/YYYY"),
      date: formatDate(new Date(), "MM/DD/YYYY"),
      // Empty arrays mean "all" for that dimension — which is why the allowed
      // ids are sent explicitly when nothing is picked. Narrowing only the
      // dropdown would leave "All Branches" quietly returning the excluded
      // branches and the branches on the other week cycle.
      filters: {
        customerIds: filters.customerIds,
        branchIds: filters.branchIds.length ? filters.branchIds : allowedBranchIds,
        locationIds: filters.locationIds,
        technicianIds: filters.technicianIds,
      },
    });
  };

  /**
   * Record the decision to measure one technician-week as two, or undo it.
   *
   * Optimistic, and rolled back if the save fails: the figures on screen would
   * otherwise show a decision the server never accepted, and a payroll screen
   * that quietly disagrees with what was stored is worse than one that reports
   * the failure.
   */
  const toggleSplit = (slice) => {
    // Every week that holds hours from both calendars, not only the ones the
    // calendars would file differently. The button is offered on the wider
    // test, so it has to act on the wider test — gating it on the narrower one
    // left it doing nothing, silently, on exactly the weeks it was shown for.
    const weeks = slice.crossoverWeekStarts ?? [];
    if (!weeks.length || slice.technicianId == null) {
      toast("Nothing to split for this technician in this range.");
      return;
    }
    const keys = weeks.map((week) => `${slice.technicianId}|${week}`);
    const turningOn = !keys.every((key) => splitTechnicians.has(key));

    setSplitTechnicians((previous) => {
      const next = new Set(previous);
      for (const key of keys) {
        if (turningOn) next.add(key);
        else next.delete(key);
      }
      return next;
    });
    setSavingSplit(true);

    Promise.all(
      weeks.map((week) => ReportService.setCycleSplit(slice.technicianId, week, turningOn)),
    )
      .then(() => loadHours())
      .catch((error) => {
        setSplitTechnicians((previous) => {
          const next = new Set(previous);
          for (const key of keys) {
            if (turningOn) next.delete(key);
            else next.add(key);
          }
          return next;
        });
        handleError(error);
      })
      .finally(() => setSavingSplit(false));
  };

  const toggleTechnician = (name) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };


  // Every technician on screen, one per page, for the browser's print dialog.
  //
  // Printed in the order the table is showing rather than always by name: a
  // stack of paper that comes out in a different order to the screen it was
  // printed from has to be re-sorted by hand before it can be filed.
  const printTimecards = () => {
    if (!sortedTechnicians.length) return;
    if (!openPrintTimecards(sortedTechnicians, loadedRange, historyByUser, onCallByUser, signoffByUser)) {
      toast("Your browser blocked the print window. Allow pop-ups for this site and try again.");
    }
  };

  const weekRange = useMemo(() => presetRange(dateRange, weekCycle), [dateRange, weekCycle]);

  const hasRun = args !== null;

  return (
    <div className="w-full py-4">
      {/* The page title is already in the top bar, so this row carries only
          the one-line description and the primary actions. It sticks under
          the top bar as the results scroll, so Load Hours, Export and Print
          stay reachable from anywhere in a long list. */}
      {/* Fixed to the viewport rather than sticky: the page column is a
          full-height flex box and a sticky child inside it re-pins every
          frame, which read as a bounce while scrolling. A spacer of the same
          height keeps the content from sliding under the bar. */}
      <div className="h-[60px] -mt-4 mb-2" aria-hidden="true" />
      <div
        className="fixed z-30 right-0 h-[60px] px-5 flex flex-wrap items-center justify-between gap-3 bg-white border-b border-[#E2E8F0] shadow-[0_2px_6px_rgba(16,24,40,0.06)]"
        style={{ top: 60, left: 257 }}
      >
        <p className="text-[13px] leading-[18px] font-InterRegular text-[#64748B] flex items-center gap-2">
          Hours logged per technician across all {vocabularize("work orders")} in a date range.
          {refreshing ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-[2px] rounded-[6px] bg-[#EEF4FF] text-[#0058E5] text-[11px] leading-4 font-InterMedium">
              <Loader variant="inline" ariaLabel="updating" />
              Updating
            </span>
          ) : null}
        </p>
        <div className="flex items-center gap-2">
          {/* Each button carries its own icon and tint so the four tell apart
              at a glance: the two rule drawers are settings (amber), Load
              Hours is the primary action (solid blue), Print is output
              (blue outline). */}
          <button
            type="button"
            onClick={() => setOtRulesOpen(true)}
            title="Which overtime and double-time policies each branch and person is paid under"
            className="h-[36px] px-3.5 rounded-lg border border-[#F5C76A] bg-[#FFF7E6] text-[#92400E] text-[14px] font-InterSemiBold inline-flex items-center gap-2 hover:bg-[#FDEBC8] hover:border-[#E5A93B] shadow-[0_1px_2px_rgba(16,24,40,0.06)]"
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
              <path d="M8 4.75V8l2.25 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M12.5 2.5l1.5 1.5M3.5 2.5L2 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            Overtime Policies
          </button>
          <button
            type="button"
            onClick={() => setLunchRulesOpen(true)}
            title="Whether lunch can be recorded, and whether it must be, per branch"
            className="h-[36px] px-3.5 rounded-lg border border-[#F5C76A] bg-[#FFF7E6] text-[#92400E] text-[14px] font-InterSemiBold inline-flex items-center gap-2 hover:bg-[#FDEBC8] hover:border-[#E5A93B] shadow-[0_1px_2px_rgba(16,24,40,0.06)]"
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4.5 2v12M3 2v3.5a1.5 1.5 0 003 0V2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M11.5 2c-1.4 0-2.5 1.6-2.5 3.5S10.1 9 11.5 9V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Lunch Policies
          </button>
          <button
            type="button"
            onClick={() => setHoursSystemOpen(true)}
            title="Which hours system each technician is on: manual entry or derived hours, switched per branch or per person"
            className="h-[36px] px-3.5 rounded-lg border border-[#F5C76A] bg-[#FFF7E6] text-[#92400E] text-[14px] font-InterSemiBold inline-flex items-center gap-2 hover:bg-[#FDEBC8] hover:border-[#E5A93B] shadow-[0_1px_2px_rgba(16,24,40,0.06)]"
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="1.75" y="4.25" width="12.5" height="7.5" rx="3.75" stroke="currentColor" strokeWidth="1.5" />
              <circle cx="10" cy="8" r="2.25" fill="currentColor" />
            </svg>
            Hours System
          </button>
          <button
            type="button"
            onClick={loadHours}
            disabled={loading}
            className="h-[36px] px-4 rounded-lg bg-[#0065FD] text-white text-[14px] font-InterSemiBold disabled:opacity-60 inline-flex items-center gap-2 hover:bg-[#0058E5] shadow-[0_1px_2px_rgba(0,101,253,0.35)]"
          >
            {loading ? (
              <Loader variant="button" />
            ) : (
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M2.5 8a5.5 5.5 0 019.4-3.9M13.5 8a5.5 5.5 0 01-9.4 3.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <path d="M12 1.5v3h-3M4 14.5v-3h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
            {loading ? "Loading..." : "Load Hours"}
          </button>
          {/* Payroll prints these and files a copy per employee, so the whole
              stack comes off one print job rather than opening each
              technician's file in turn. */}
          {visibleTechnicians.length > 0 && !loading ? (
            <button
              type="button"
              onClick={printTimecards}
              title={`Printable breakdown for all ${sortedTechnicians.length} technicians, one per page`}
              className="h-[36px] px-3.5 rounded-lg border border-[#7FAFFF] bg-[#EEF4FF] text-[#0065FD] text-[14px] font-InterSemiBold inline-flex items-center gap-2 hover:bg-[#DCE8FF] hover:border-[#0065FD] shadow-[0_1px_2px_rgba(16,24,40,0.06)]"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4.5 6V2.5h7V6M4.5 11.5h7V14h-7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                <rect x="2" y="6" width="12" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.5" />
              </svg>
              Print Timecards
            </button>
          ) : null}
        </div>
      </div>

      <div className="px-5 mt-3">
        <div className="rounded-xl border border-[#CBD5E1] bg-white p-4 shadow-[0_1px_3px_rgba(16,24,40,0.08)]">
          {/* The cycle sits with the date controls because that is what it
              governs: it decides which days "This Week" covers. */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[13px] leading-5 font-InterMedium text-[#45556C]">Week cycle</span>
            <div className="flex items-center gap-1">
              {WEEK_CYCLE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setWeekCycle(option.value)}
                  className={`h-8 px-3 rounded-[6px] text-[13px] leading-[18px] ring-1 ring-inset transition-colors ${
                    weekCycle === option.value
                      ? "bg-[#EAF3FF] ring-[#0065FD] text-[#0065FD] font-InterSemiBold"
                      : "bg-white ring-[#CBD5E1] text-[#5F6477] font-InterRegular hover:bg-[#F7F9FC]"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <span className="text-[12px] leading-4 font-InterRegular text-[#94A3B8]">
              {cycleBranchIds.length} {cycleBranchIds.length === 1 ? "branch" : "branches"}{" "}
              {isBothCycles(weekCycle) ? "across both cycles" : "on this cycle"}
            </span>
          </div>

          <DateRangeSelector
            asDropdown
            presets={TIME_PRESETS}
            rangeStart={weekRange?.[0]}
            rangeEnd={weekRange?.[1]}
            selected={dateRange}
            onSelect={setDateRange}
            customStart={customStart}
            customEnd={customEnd}
            onCustomChange={(dates) => {
              const [start, end] = Array.isArray(dates) ? dates : [null, null];
              setCustomStart(start);
              setCustomEnd(end);
            }}
          />
          <div className="mt-3">
            <ReportFiltersSection
              value={filters}
              onChange={setFilters}
              shown
              allowedBranchIds={cycleBranchIds}
              branchLabelAccessory={
                <BranchExclusionEditor
                  branches={branches}
                  excludedIds={excludedBranchIds}
                  onChange={saveExclusions}
                  saving={savingExclusions}
                />
              }
              containerClassName="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-3 gap-y-3"
            />
          </div>
        </div>
      </div>

      {/* What this screen can and cannot show, stated where it is read rather
          than left for someone to infer from the numbers. Kept to one line so a
          permanent caveat does not cost a permanent block of screen. */}

      {loading ? (
        <div className="px-5 mt-3">
          <div className="rounded-xl border border-[#CBD5E1] bg-white px-5 py-4 shadow-[0_1px_3px_rgba(16,24,40,0.08)]">
            <div className="text-[13px] font-InterMedium text-[#64748B]">Loading hours...</div>
            {/* Indeterminate by necessity: the range arrives in one response,
                and nearly all the wait is the server querying, so there are no
                bytes to measure against. The segment travels rather than
                pulsing in place — a bar sitting at a fixed width reads as a
                determinate one that has stalled. */}
            <div className="relative mt-2 h-[6px] w-full rounded-full bg-[#E5E8F3] overflow-hidden">
              <div className="absolute top-0 h-full w-2/5 rounded-full bg-[#0065FD] animate-indeterminateBar" />
            </div>
          </div>
        </div>
      ) : null}

      {preservedCount > 0 ? (
        <div className="px-5 mt-2">
          <div className="rounded-md border border-[#BFDBFE] bg-[#EFF6FF] px-3 py-1.5 text-[12px] leading-[18px] font-InterRegular text-[#1E40AF]">
            {preservedCount} {preservedCount === 1 ? "technician has" : "technicians have"} days where overtime was
            already entered on the timesheet. Those days are shown exactly as entered and were not recalculated —
            they may pre-date the current rule, or come from a weekend or manual adjustment this calculation does not
            model.
          </div>
        </div>
      ) : null}


      {/* Two different messages, because the honest one depends on which
          version of the endpoint answered. A server that derives its summary
          over the whole range says so; one that does not leaves the field
          absent, and this screen must not vouch for totals it cannot stand
          behind — the figures get keyed into payroll. */}
      {capped && totalsComplete ? (
        <div className="px-5 mt-4">
          <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-[13px] leading-5 font-InterRegular text-[#92400E]">
            <span className="font-InterSemiBold">Totals below are complete</span> and cover every one of the{" "}
            {recordsFound.toLocaleString()} entries in this range. Only the per-entry breakdown is short: the first{" "}
            {maxRows.toLocaleString()} entries were loaded, so some technicians cannot be expanded. Narrow the range,
            or filter to a branch or technician, to see their detail.
          </div>
        </div>
      ) : null}

      {capped && !totalsComplete ? (
        <div className="px-5 mt-4">
          <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-[13px] leading-5 font-InterRegular text-[#991B1B]">
            <span className="font-InterSemiBold">
              Totals below are incomplete — do not use them for payroll.
            </span>{" "}
            Only the first {maxRows.toLocaleString()} of {recordsFound.toLocaleString()} entries were loaded, and
            because entries are ordered by technician name, technicians later in the alphabet may be missing from this
            list entirely rather than merely short. Narrow the range, or filter to a branch or technician, and load
            again.
          </div>
        </div>
      ) : null}

      {/* Crossover: hours on branches from both week calendars. Their overtime
          is still measured on one cycle — their own branch's — so this is not a
          figure to correct but a judgement to hand to payroll.

          Two messages, because what is useful differs. Showing one cycle, the
          people on the *other* one are not on screen at all, so the flag has to
          name them and say what is missing. Showing both, they are right there
          and the flag only has to point. */}
      {crossovers.length > 0 && !isBothCycles(weekCycle) ? (
        <div className="px-5 mt-4">
          <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-[13px] leading-5 font-InterRegular text-[#92400E]">
            <span className="font-InterSemiBold">
              {crossovers.length} {crossovers.length === 1 ? "technician has" : "technicians have"} hours the two week
              calendars would file into different weeks
            </span>{" "}
            — {crossovers.map((tech) => tech.technician).join(", ")}.{" "}
            {visibleCrossovers.length < crossovers.length
              ? "Some are measured on the other cycle and are not listed here. "
              : ""}
            Switch the week cycle to <span className="font-InterSemiBold">Both</span> and load again to see the split.
          </div>
        </div>
      ) : null}

      {visibleCrossovers.length > 0 && isBothCycles(weekCycle) ? (
        <div className="px-5 mt-4">
          <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-[13px] leading-5 font-InterRegular text-[#92400E]">
            <p>
              <span className="font-InterSemiBold">
                {visibleCrossovers.length} {visibleCrossovers.length === 1 ? "technician" : "technicians"} worked
                branches on both week cycles.
              </span>{" "}
              The two calendars would file the days below into different weeks, so which week these hours belong to
              is a payroll decision rather than something the derivation can settle. Hours on both cycles that both
              calendars agree about are not listed — they land in the same week either way.
            </p>
            <ul className="mt-1.5 list-disc pl-5">
              {visibleCrossovers.map((tech) => (
                <li key={tech.technician}>
                  <span className="font-InterSemiBold">{tech.technician}</span> —{" "}
                  {(tech.crossoverConflicts ?? [])
                    .map(
                      (hit) =>
                        `${hit.date} on ${hit.branch || "an unnamed branch"} (${
                          hit.cycle === WEEK_CYCLE_MON_SUN ? "Mon\u2013Sun" : "Sun\u2013Sat"
                        })`,
                    )
                    .join(", ")}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      {visibleTechnicians.length > 0 ? (
        <div className="px-5 mt-3 flex gap-3 flex-wrap">
          <SummaryTile label="RT (hrs)" value={formatHhMm(totals.regularMinutes)} />
          <SummaryTile label="OT (hrs)" value={formatHhMm(totals.overMinutes)} />
          <SummaryTile label="DT (hrs)" value={formatHhMm(totals.doubleMinutes)} />
          <SummaryTile label="TT (hrs)" value={formatHhMm(totals.travelMinutes)} muted />
          <SummaryTile label="On call (days)" value={String(onCallDayTotal)} />
          {signoffTotals.weeks > 0 ? (
            <SummaryTile
              label="Cards submitted"
              value={`${signoffTotals.submitted} / ${signoffTotals.weeks}`}
              sublabel={
                signoffTotals.requests
                  ? `${signoffTotals.requests} reopen ${signoffTotals.requests === 1 ? "request" : "requests"}`
                  : signoffTotals.submitted === signoffTotals.weeks
                    ? "every finished week"
                    : `${signoffTotals.weeks - signoffTotals.submitted} outstanding`
              }
            />
          ) : null}
          <SummaryTile label="Total (hrs)" value={formatHhMm(totals.totalMinutes)} />
          <SummaryTile
            label="Technicians"
            sublabel={`${visibleRowCount} entries`}
            value={String(visiblePeople)}
            muted
          />
        </div>
      ) : null}

      <div className="px-5 mt-3">
        {!hasRun ? (
          <div className="text-[14px] font-InterRegular text-borderColor">
            Choose a date range and select Load Hours.
          </div>
        ) : !loading && technicians.length === 0 ? (
          <div className="text-[14px] font-InterRegular text-borderColor">No hours logged in this range.</div>
        ) : visibleTechnicians.length === 0 ? (
          <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-[13px] leading-5 font-InterRegular text-[#92400E]">
            <span className="font-InterSemiBold">
              No hours were recorded against branches on this week cycle.
            </span>{" "}
            {technicians.length} {technicians.length === 1 ? "technician" : "technicians"} logged{" "}
            {rows.length} {rows.length === 1 ? "entry" : "entries"} in this range, all against branches on the other
            cycle. Switch the week cycle, or choose <span className="font-InterSemiBold">Both</span>, to see them.
          </div>
        ) : visibleTechnicians.length > 0 ? (
          <>
          {/* A save has gone through and the figures are being fetched again.
              Said plainly with the same travelling bar as the first load, and
              the table dimmed beneath it, so nobody reads the old numbers as
              the new ones in the second or two before they change. */}
          {refreshing ? (
            <div className="mb-3 rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] px-5 py-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[13px] leading-[18px] font-InterSemiBold text-[#1E40AF]">
                  Change saved — updating the figures…
                </p>
                <p className="text-[12px] leading-[18px] font-InterRegular text-[#3B82F6]">
                  Totals, overtime and history refresh together.
                </p>
              </div>
              <div className="relative mt-2 h-[6px] w-full rounded-full bg-[#DBEAFE] overflow-hidden">
                <div className="absolute top-0 h-full w-2/5 rounded-full bg-[#0065FD] animate-indeterminateBar" />
              </div>
            </div>
          ) : null}
          <div
            className={`w-full rounded-xl border border-[#CBD5E1] bg-white shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05),0_2px_4px_-1px_rgba(0,0,0,0.03)] overflow-hidden transition-opacity duration-200 ${
              refreshing ? "opacity-60 pointer-events-none" : ""
            }`}
          >
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[820px] text-left border-collapse">
                <thead>
                  <tr className="bg-[#EEF2F7] border-b border-[#CBD5E1]">
                    <th className="px-6 py-4 text-[12px] leading-4 font-InterBold text-[#1F2A44] uppercase tracking-[0.6px]">
                      Technician
                    </th>
                    {SORT_COLUMNS.map((column) => (
                      <SortableHeader key={column.key} column={column} sort={sort} onSort={toggleSort} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* With both calendars on screen the rows are grouped under
                      the cycle each technician is measured on, with its own
                      subtotal. Grouping by the branch a row belongs to instead
                      would list a crossover technician twice and show each half
                      of their week against a threshold it never met. */}
                  {cycleSections.map((section) => (
                    <Fragment key={section.cycle}>
                      {cycleSections.length > 1 ? (
                        <tr className="bg-[#EEF2F7] border-y border-[#E2E8F0]">
                          <td
                            colSpan={7}
                            className="px-6 py-2 text-[12px] leading-4 font-InterBold text-[#45556C] uppercase tracking-[0.6px]"
                          >
                            {section.label} · {section.technicians.length}{" "}
                            {section.technicians.length === 1 ? "technician" : "technicians"}
                          </td>
                        </tr>
                      ) : null}

                      {section.technicians.map((tech) => (
                        <TechnicianRow
                          key={`${tech.technician}|${tech.cycle}`}
                          technician={tech}
                          expanded={expanded.has(`${tech.technician}|${tech.cycle}`)}
                          onToggle={() => toggleTechnician(`${tech.technician}|${tech.cycle}`)}
                          onToggleSplit={() => toggleSplit(tech)}
                          savingSplit={savingSplit}
                          range={loadedRange}
                          onOpenHistory={setHistoryFor}
                          onHoursSaved={refreshHours}
                          historyByUser={historyByUser}
                          onCall={onCallByUser[tech.technicianId]}
                          weekCycle={weekCycle}
                          // Every branch's rows are already loaded; only the
                          // display is narrowed by cycle, so widening it is a
                          // state change, not a reload.
                          onShowBothCycles={() => setWeekCycle(WEEK_CYCLE_BOTH)}
                          signoff={signoffByUser[tech.technicianId]}
                          onSignoffChanged={() => {
                            setSignoffRefresh((value) => value + 1);
                            setHistoryRefresh((value) => value + 1);
                          }}
                        />
                      ))}

                      {cycleSections.length > 1 ? (
                        <tr className="bg-[#EEF2F7] border-b border-[#CBD5E1]">
                          <td className="px-6 py-3 text-[13px] font-InterSemiBold text-[#45556C]">
                            {section.label} subtotal
                          </td>
                          <td className="px-6 py-3" />
                          <td className="px-6 py-3 text-center text-[13px] font-InterSemiBold text-[#1F2A44]">
                            {formatHhMm(section.totals.regularMinutes)}
                          </td>
                          <td className="px-6 py-3 text-center text-[13px] font-InterSemiBold text-[#1F2A44]">
                            {formatHhMm(section.totals.overMinutes)}
                          </td>
                          <td className="px-6 py-3 text-center text-[13px] font-InterSemiBold text-[#1F2A44]">
                            {formatHhMm(section.totals.doubleMinutes)}
                          </td>
                          <td className="px-6 py-3 text-center text-[13px] font-InterSemiBold text-[#64748B]">
                            {formatHhMm(section.totals.travelMinutes)}
                          </td>
                          <td className="px-6 py-3 text-center text-[13px] font-InterBold text-[#1F2A44]">
                            {formatHhMm(section.totals.totalMinutes)}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                  <tr className="bg-[#EEF2F7] border-t border-[#CBD5E1]">
                    <td className="px-6 py-4 text-[14px] font-InterBold text-[#1F2A44]">All technicians</td>
                    <td className="px-6 py-4 text-center text-[14px] font-InterMedium text-[#64748B]">{visibleRowCount}</td>
                    <td className="px-6 py-4 text-center text-[14px] font-InterBold text-[#1F2A44]">
                      {formatHhMm(totals.regularMinutes)}
                    </td>
                    <td className="px-6 py-4 text-center text-[14px] font-InterBold text-[#1F2A44]">
                      {formatHhMm(totals.overMinutes)}
                    </td>
                    <td className="px-6 py-4 text-center text-[14px] font-InterBold text-[#1F2A44]">
                      {formatHhMm(totals.doubleMinutes)}
                    </td>
                    <td className="px-6 py-4 text-center text-[14px] font-InterBold text-[#64748B]">
                      {formatHhMm(totals.travelMinutes)}
                    </td>
                    <td className="px-6 py-4 text-center text-[14px] font-InterBold text-[#5B21B6] whitespace-nowrap">
                      {onCallDayTotal}
                      <span className="ml-1 text-[11px] font-InterMedium text-[#7C3AED]">
                        {onCallDayTotal === 1 ? "day" : "days"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-[14px] font-InterBold text-[#1F2A44]">
                      {formatHhMm(totals.totalMinutes)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          </>
        ) : null}
      </div>
      <OtRuleDrawer
        isOpen={otRulesOpen}
        onClose={() => {
          setOtRulesOpen(false);
          setHistoryRefresh((n) => n + 1);
        }}
      />
      <LunchRulesDrawer isOpen={lunchRulesOpen} onClose={() => setLunchRulesOpen(false)} />
      <HoursSystemDrawer
        isOpen={hoursSystemOpen}
        onClose={() => {
          setHoursSystemOpen(false);
          // The figures themselves change with the system, so the range
          // reloads the way it does after an edit.
          setHistoryRefresh((n) => n + 1);
          loadHours();
        }}
      />
      <HoursHistoryDrawer
        technician={historyFor}
        range={loadedRange}
        preloaded={historyFor ? historyByUser[historyFor.technicianId] : undefined}
        onClose={() => setHistoryFor(null)}
      />
    </div>
  );
};

export default TimeManagement;
